import React, { useRef, useState, useEffect } from 'react';
import { Slider } from 'antd';
import { PlayCircleOutlined, PauseCircleOutlined } from '@ant-design/icons';
import styles from './VideoPlayer.module.scss';
import VideoElement from './VideoElement';
import VideoControls from './VideoControls';
import SeekBar from './SeekBar';
import RangeSlider from './RangeSlider';
import DrawingCanvas, { Shape, DrawingTool } from './DrawingCanvas';

export type { DrawingTool, Shape } from './DrawingCanvas';

interface VideoPlayerProps {
  src: string;
  fileType?: 'video' | 'image';
  isDrawingEnabled: boolean;
  title?: string;
  poster?: string;
  
  // Drawing props
  shapes: Shape[];
  tool: DrawingTool;
  color: string;
  onShapesChange: (shapes: Shape[]) => void;
  onTextToolClick: (pos: { x: number, y: number }) => void;
  selectedShapeId?: string | null;
  onShapeSelection?: (shapeId: string | null) => void;
  onDrawingComplete?: () => void;
  isDrawingToolboxOpen?: boolean;
  onCurrentTimeChange?: (time: number, fps: number) => void;
  currentVideoTime?: number;
  currentFrameIndex?: number;
  isPaused?: boolean;
  onPlay?: () => void;
  onSeek?: () => void;
  onDurationChange?: (duration: number) => void;
  selectedRange?: [number, number] | null;
  onRangeSelect?: (range: [number, number]) => void;
  externalRange?: [number, number] | null;
  
  // Comments props
  comments?: Array<{
    id: number;
    startFrame: number;
    endFrame: number;
    text: string;
    shapes?: Shape[];
    user?: {
      firstName: string;
      lastName: string;
    };
    isResolved?: boolean;
  }>;
  onCommentClick?: (commentId: number) => void;
  selectedCommentId?: number | null;
  fps?: number;
  
  // Compare mode prop
  compareMode?: boolean;

  // Show resolved pins
  showResolved?: boolean;
}

const VideoPlayer: React.FC<VideoPlayerProps> = ({
  src,
  fileType = 'video',
  isDrawingEnabled,
  title,
  poster,
  shapes,
  tool,
  color,
  onShapesChange,
  onTextToolClick,
  selectedShapeId,
  onShapeSelection,
  onDrawingComplete,
  isDrawingToolboxOpen = false,
  onCurrentTimeChange,
  currentVideoTime,
  currentFrameIndex,
  isPaused,
  onPlay,
  onSeek,
  onDurationChange,
  selectedRange,
  onRangeSelect,
  externalRange,
  comments = [],
  onCommentClick,
  selectedCommentId,
  fps = 30,
  compareMode = false,
  showResolved,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [volume, setVolume] = useState(1);
  const [controlsVisible, setControlsVisible] = useState(false);
  const [isLooping, setIsLooping] = useState(false);

  // Custom seekbar için
  const seekbarRef = useRef<HTMLDivElement>(null);

  // Range seçme modunda mini slider
  const [showRangeSlider, setShowRangeSlider] = useState(false);
  const [rangeSliderValue, setRangeSliderValue] = useState<[number, number]>([0, 0]);
  
  // selectedRange veya externalRange prop'u değiştiğinde rangeSliderValue'yu güncelle ve range bar'ı göster
  useEffect(() => {
    // externalRange öncelikli
    const activeRange = externalRange || selectedRange;
    
    if (activeRange) {
      if (activeRange[0] === 0 && activeRange[1] === 0) {
        // [0, 0] ise range bar'ı gizle
        setShowRangeSlider(false);
        setRangeSliderValue([0, 0]);
      } else {
        // Geçerli bir range ise göster
        setRangeSliderValue(activeRange);
        setShowRangeSlider(true);
      }
    } else {
      // Hiç range yoksa range bar'ı gizle
      setShowRangeSlider(false);
      setRangeSliderValue([0, 0]);
    }
  }, [selectedRange, externalRange]);

  // Duration değiştiğinde range bar'ı sıfırla (sadece selectedRange ve externalRange yoksa)
  useEffect(() => {
    if (duration > 0 && !selectedRange && !externalRange) {
      setShowRangeSlider(false);
      setRangeSliderValue([0, 0]);
    }
  }, [duration, selectedRange, externalRange]);
  
  // Looping state'i video elementine uygula
  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.loop = !!isLooping;
    }
  }, [isLooping]);

  const handlePlayPause = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
        // Video durdurulduğunda range set et - seekbar mantığı ile aynı
        const currentTime = videoRef.current.currentTime;
        onRangeSelect?.([currentTime, currentTime]);
        // Range slider value'sunu da güncelle
        setRangeSliderValue([currentTime, currentTime]);
        // Video pause edildiğinde range slider'ı göster
        setShowRangeSlider(true);
      } else {
        videoRef.current.play();
        onPlay?.();
        // Video play edildiğinde range slider'ı gizle
        setShowRangeSlider(false);
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleTimeUpdate = () => {
    if (videoRef.current) {
      setCurrentTime(videoRef.current.currentTime);
      // FPS ve currentTime'ı parent'a bildir
      onCurrentTimeChange?.(videoRef.current.currentTime, fps);
    }
  };

  const [stageSize, setStageSize] = useState({ width: 0, height: 0 });

  // Video boyutunu güncelleyen fonksiyon
  const updateStageSize = () => {
    if (videoRef.current) {
      setStageSize({
        width: videoRef.current.clientWidth,
        height: videoRef.current.clientHeight,
      });
    } else if (fileType === 'image' && imgRef.current) {
      // İmaj dosyaları için img element boyutunu kullan
      setStageSize({
        width: imgRef.current.clientWidth,
        height: imgRef.current.clientHeight,
      });
    }
  };

  // Video yüklendiğinde ve pencere yeniden boyutlandığında güncelle
  useEffect(() => {
    updateStageSize();
    window.addEventListener('resize', updateStageSize);
    return () => window.removeEventListener('resize', updateStageSize);
  }, []);

  // Video boyutu değiştiğinde canvas boyutunu güncelle
  useEffect(() => {
    const resizeObserver = new ResizeObserver(() => {
      updateStageSize();
    });
    
    if (videoRef.current) {
      resizeObserver.observe(videoRef.current);
    }
    
    // Video container'ın parent element'ini de observe et
    if (videoRef.current?.parentElement) {
      resizeObserver.observe(videoRef.current.parentElement);
    }
    
    // İmaj dosyaları için container'ı observe et
    if (fileType === 'image' && imgRef.current) {
      resizeObserver.observe(imgRef.current);
    }
    
    return () => {
      resizeObserver.disconnect();
    };
  }, [fileType]);

  // Compare mode değiştiğinde canvas boyutunu güncelle
  useEffect(() => {
    if (compareMode) {
      // Compare mode'a geçişte canvas boyutunu güncellemek için birkaç deneme yap
      const updateSize = () => {
        updateStageSize();
      };
      
      // İlk güncelleme
      const timer1 = setTimeout(updateSize, 100);
      
      // İkinci güncelleme (daha güvenilir için)
      const timer2 = setTimeout(updateSize, 300);
      
      // Üçüncü güncelleme (layout tamamlandıktan sonra)
      const timer3 = setTimeout(updateSize, 500);
      
      return () => {
        clearTimeout(timer1);
        clearTimeout(timer2);
        clearTimeout(timer3);
      };
    }
  }, [compareMode]);

  // Video metadata yüklendiğinde de güncelle
  const handleLoadedMetadata = () => {
    if (videoRef.current) {
      setDuration(videoRef.current.duration);
      onDurationChange?.(videoRef.current.duration);
      updateStageSize();
    } else if (fileType === 'image' && imgRef.current) {
      // İmaj dosyaları için sadece boyut güncelle
      updateStageSize();
    }
  };
  
  const handleVolumeChange = (value: number) => {
    if (videoRef.current) {
      videoRef.current.volume = value;
      setVolume(value);
    }
  };
  
  const handleFullScreen = () => {
    if (videoRef.current?.parentElement && videoRef.current.parentElement.requestFullscreen) {
        videoRef.current.parentElement.requestFullscreen();
    }
  };

  React.useEffect(() => {
    // Sadece dışarıdan gelen currentVideoTime değeri varsa ve video'nun kendi currentTime'ından farklıysa seek et
    if (currentVideoTime !== undefined && videoRef.current) {
      const timeDifference = Math.abs(videoRef.current.currentTime - currentVideoTime);
      if (timeDifference > 0.1) { // 100ms'den fazla fark varsa seek et
        videoRef.current.currentTime = currentVideoTime;
        setCurrentTime(currentVideoTime);
      }
    }
  }, [currentVideoTime]);

  // Seekbar'a tıklayınca range seçme modunu başlat
  const handleSeekbarClick = (e: React.MouseEvent<HTMLDivElement> | { clientX: number }) => {
    if (!duration || !videoRef.current || !seekbarRef.current) return;
    const rect = seekbarRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percent = Math.max(0, Math.min(1, x / rect.width));
    const time = percent * duration;
    
    // Video'yu yeni pozisyona seek et
    videoRef.current.currentTime = time;
    setCurrentTime(time);
    onSeek?.();
    
    // Mor çubuğa tıklandığında onRangeSelect çağır - tek frame seçimi
    onRangeSelect?.([time, time]);
    
    // Range seçme modunu başlat - başlangıç ve bitiş aynı noktada
    setShowRangeSlider(true);
    setRangeSliderValue([time, time]); // Başlangıç ve bitiş aynı noktada
  };

  // Seekbar'da slide ederken video oynat
  const handleSeekbarDrag = (e: React.MouseEvent<HTMLDivElement> | { clientX: number }) => {
    if (!duration || !videoRef.current || !seekbarRef.current) return;
    const rect = seekbarRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percent = Math.max(0, Math.min(1, x / rect.width));
    const time = percent * duration;
    
    // Video'yu yeni pozisyona seek et
    videoRef.current.currentTime = time;
    setCurrentTime(time);
    onSeek?.();
    
    // Sürükleme sırasında da frame seçimi yap
    onRangeSelect?.([time, time]);
  };



  // Range slider'ı kapatmak için video'ya tıklama
  const handleVideoClick = (e: React.MouseEvent) => {
    // Drawing toolbox açıksa tıklamayı engelle
    if (isDrawingToolboxOpen) {
      return;
    }
    
    // Video henüz yüklenmemişse tıklamayı engelle
    if (!videoRef.current || duration === 0) {
      return;
    }
    
    // Eğer range slider açıksa kapat
    if (showRangeSlider) {
      setShowRangeSlider(false);
    }
    
    // Her zaman play/pause yap
    handlePlayPause();
  };

  // currentFrameIndex değiştiğinde FPS ile tam frame'e seek et
  useEffect(() => {
    if (
      typeof currentFrameIndex === 'number' &&
      videoRef.current
    ) {
      // FPS değeri 0 ise veya çok düşükse, varsayılan 30 FPS kullan
      const effectiveFps = fps > 10 ? fps : 30;
      const seekTime = currentFrameIndex / effectiveFps;
      
      // Video'nun mevcut zamanından çok farklıysa seek et
      if (Math.abs(videoRef.current.currentTime - seekTime) > 0.01) {
        videoRef.current.currentTime = seekTime;
        setCurrentTime(seekTime);
      }
    }
  }, [currentFrameIndex, fps]);

  // isPaused prop'una göre video'yu durdur/oynat
  useEffect(() => {
    if (videoRef.current) {
      if (isPaused) {
        videoRef.current.pause();
        setIsPlaying(false);
      }
    }
  }, [isPaused]);
  // Keyboard shortcuts - Space tuşu ile video durdur/başlat
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.code === 'Space' && !e.ctrlKey && !e.metaKey && !e.altKey) {
        const target = e.target as HTMLElement;
        if (target && (
          target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.tagName === 'SELECT' ||
          target.contentEditable === 'true'
        )) {
          return;
        }
        e.preventDefault();
        handlePlayPause();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isPlaying, duration]);

  return (
    <>
      <div
        className={styles.playerWrapper}
        onMouseEnter={() => setControlsVisible(true)}
        onMouseLeave={() => setControlsVisible(false)}
        onClick={handleVideoClick}
      >
        <VideoElement
          ref={fileType === 'image' ? imgRef : videoRef}
          src={src}
          fileType={fileType}
          poster={poster}
          isDrawingEnabled={isDrawingEnabled}
          isDrawingToolboxOpen={isDrawingToolboxOpen}
          onTimeUpdate={handleTimeUpdate}
          onLoadedMetadata={handleLoadedMetadata}
          onEnded={() => setIsPlaying(false)}
          onVideoClick={handleVideoClick}
          onError={(e) => {
            console.error('Video yükleme hatası:', e);
            console.error('Video src:', src);
            const videoElement = e.target as HTMLVideoElement;
            console.error('Video error details:', videoElement.error);
          }}
        />
        
        {/* Play/Pause İkonları Overlay - Tıklanabilir */}
        {fileType === 'video' && (
          <div 
            className={`${styles.playPauseOverlay} ${controlsVisible ? styles.visible : ''} ${isDrawingToolboxOpen ? styles.disabled : ''}`}
            onClick={isDrawingToolboxOpen ? undefined : handleVideoClick}
          >
            {isPlaying ? (
              <PauseCircleOutlined className={styles.playPauseIcon} />
            ) : (
              <PlayCircleOutlined className={styles.playPauseIcon} />
            )}
          </div>
        )}
        
        {/* DrawingCanvas'ı en üste taşı */}
        <DrawingCanvas
          stageSize={stageSize}
          shapes={shapes}
          tool={tool}
          color={color}
          selectedShapeId={selectedShapeId}
          isDrawingEnabled={isDrawingEnabled}
          isDrawingToolboxOpen={isDrawingToolboxOpen}
          onShapesChange={onShapesChange}
          onTextToolClick={onTextToolClick}
          onShapeSelection={onShapeSelection}
          onDrawingComplete={onDrawingComplete}
        />
      </div>

      {/* Kontrol overlay'ini video container'ının dışına taşı */}
      {fileType === 'video' && (
        <div className={`${styles.controlsOverlay} ${controlsVisible || !isPlaying ? styles.visible : ''}`}>
          {/* {title && <div className={styles.title}>{title}</div>} */}
          {/* Barlar container */}
          <div 
            style={{ position: 'relative', width: '100%', margin: '0 0 8px 0', height: 20, zIndex: 1000 }}
            onTouchStart={(e) => e.stopPropagation()}
            onTouchMove={(e) => e.stopPropagation()}
            onTouchEnd={(e) => e.stopPropagation()}
          >
            {/* Mor seekbar (video ilerleme çubuğu) */}
            <SeekBar
              seekbarRef={seekbarRef}
              currentTime={currentTime}
              duration={duration}
              comments={comments}
              fps={fps || 30}
              selectedCommentId={selectedCommentId}
              onSeekbarClick={handleSeekbarClick}
              onSeekbarDrag={handleSeekbarDrag}
              onCommentClick={onCommentClick}
              showResolved={showResolved}
            />
            {/* Yeşil range bar (altında, daha ince) */}
            <RangeSlider
              showRangeSlider={showRangeSlider}
              rangeSliderValue={rangeSliderValue}
              duration={duration}
              onRangeChange={(value) => {
                const prevValue = rangeSliderValue;
                setRangeSliderValue(value);
                onRangeSelect?.(value);
                if (value[0] !== prevValue[0]) {
                  if (videoRef.current) {
                    videoRef.current.currentTime = value[0];
                    setCurrentTime(value[0]);
                    onSeek?.();
                  }
                } else if (value[1] !== prevValue[1]) {
                  if (videoRef.current) {
                    videoRef.current.currentTime = value[1];
                    setCurrentTime(value[1]);
                    onSeek?.();
                  }
                }
              }}
              onRangeChangeComplete={(value) => {
                if (videoRef.current) {
                  videoRef.current.currentTime = value[0];
                  setCurrentTime(value[0]);
                  onSeek?.();
                }
              }}
            />
          </div>
          <VideoControls
            isPlaying={isPlaying}
            volume={volume}
            currentTime={currentTime}
            duration={duration}
            onPlayPause={handlePlayPause}
            onVolumeChange={handleVolumeChange}
            onFullScreen={handleFullScreen}
            isLooping={isLooping}
            onToggleLoop={() => setIsLooping((v) => !v)}
          />
        </div>
      )}
    </>
  );
};

export default VideoPlayer; 